import { Box, Button, Flex, Input, Link, Stack, Text, Textarea } from '@chakra-ui/react';
import { PlusCircle, UploadSimple, XCircle } from '@phosphor-icons/react';
import Papa from 'papaparse';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { fetchAPI } from '@/api/fetch';
import { apiAdminSendBatchEmail, Recipient } from '@/api/services';

type FormValues = {
  recipients: Recipient[];
  subject: string;
  body: string;
};

export default function Mailer() {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const defaultBody = `Hi {{.name}},

Add the body of your email here.

Best regards,
PolarisJobs`;

  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      recipients: [{ name: '', email: '' }],
      subject: '',
      body: defaultBody
    }
  });

  const onUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);

    const files = event.target.files;

    if (!files || files.length === 0) {
      return;
    }

    Array.from(files).forEach((file) => {
      Papa.parse<Recipient>(file, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data, errors }) => {
          if (errors.length > 0) {
            setFileError(`Error parsing CSV: ${errors[0].message}`);
            return;
          }

          const extractedData = data
            .map(({ name = '', email = '' }: Recipient) => ({ name, email }))
            .filter(({ name, email }) => name && email && validateEmail(email));

          if (extractedData.length === 0) {
            setFileError('No valid name/email pairs found in the CSV file');
            return;
          }

          const currentRecipients = getValues('recipients').filter(
            ({ name, email }) => name && email
          );
          setValue('recipients', [...currentRecipients, ...extractedData]);
        },
        error: (error) => {
          setFileError(`Error parsing the CSV file: ${error.message}`);
        }
      });
    });
  };

  const validateEmail = (email: string) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);

  const handleFormSubmit = async (formData: FormValues) => {
    setFileError(null);
    setSubmitError(null);

    try {
      const emailSet = new Set();
      const duplicateEmails = formData.recipients.some(({ email }) => {
        if (emailSet.has(email)) {
          return true;
        }
        emailSet.add(email);
        return false;
      });

      if (duplicateEmails) {
        console.error('Duplicate emails found in the recipients list.');
        setSubmitError('Duplicate emails found in the recipients list.');
        return;
      }

      const response = await fetchAPI<void>({
        request: () =>
          apiAdminSendBatchEmail({
            payload: {
              recipients: formData.recipients,
              subject: formData.subject,
              body: formData.body
            }
          })
      });
      if (!response.success) {
        console.error('Failed to send email:', response.message);
        setSubmitError('Failed to send email. Please try again.');
      } else {
        reset(); // Reset the form after successful submission
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
    }
  };

  const confirmAction = (message: string, action: () => void) => {
    if (window.confirm(message)) {
      action();
    }
  };

  const clearAction = () => {
    setFileError(null);
    setSubmitError(null);
    reset();
  };

  return (
    <Flex w="100%" mt="16px" gap="24px" p="24px">
      <Stack w="100%" gap="16px">
        <Flex
          alignItems="center"
          h="48px"
          p="16px 24px"
          borderBottom="1px solid"
          borderColor="neutrals.200"
        >
          <Text fontSize="24px" fontWeight="semibold" color="neutrals.700">
            Batch Email Sender
          </Text>
        </Flex>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmAction('Are you sure you want to submit?', handleSubmit(handleFormSubmit));
          }}
        >
          <Stack gap="24px">
            <Flex gap="24px">
              <Text fontSize="16px" fontWeight="semibold" color="neutrals.700" w="10%">
                Recipients
              </Text>
              <Stack gap="16px">
                <Stack>
                  <Box
                    w="100%"
                    h="50px"
                    border="1px dashed"
                    borderColor={fileError ? 'error.700' : 'neutrals.300'}
                    borderRadius="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    bg="neutrals.50"
                    position="relative"
                  >
                    <input
                      id="resumeFile"
                      type="file"
                      accept=".csv"
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                      onChange={onUploadFile}
                    />
                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      fontSize="14px"
                      gap="10px"
                    >
                      <UploadSimple size={20} color="#404040" />
                      <Box textAlign="center" color="neutrals.900">
                        Drop files here or{' '}
                        <Box as="span" color="primary.900">
                          click to upload
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  {fileError && (
                    <Box fontSize="14px" color="error.700">
                      {fileError}
                    </Box>
                  )}
                  <Link
                    href="https://docs.google.com/spreadsheets/d/1jbGX6cpSCqxYVcPIZM7fBgnpEk2imhl-hvY82EqEhRw/edit?usp=sharing"
                    target="_blank"
                    fontSize="14px"
                    color="primary.900"
                    textDecoration="underline"
                  >
                    Access the sample spreadsheet here. (Download as CSV)
                  </Link>
                </Stack>
                {watch('recipients').map((_, index) => (
                  <Flex key={index} gap="8px">
                    <Stack gap="0">
                      <Input
                        w="200px"
                        placeholder="Name"
                        fontSize="16px"
                        bg="background.secondary"
                        disabled={isSubmitting}
                        borderColor={
                          errors.recipients?.[index]?.name ? 'error.500' : 'neutrals.200'
                        }
                        {...register(`recipients.${index}.name`, {
                          required: 'Name is required.'
                        })}
                      />
                      {errors.recipients?.[index]?.name && (
                        <Text fontSize="14px" color="error.700" mt="5px">
                          {errors.recipients[index].name.message}
                        </Text>
                      )}
                    </Stack>
                    <Stack gap="0">
                      <Input
                        w="240px"
                        placeholder="Email address"
                        fontSize="16px"
                        bg="background.secondary"
                        disabled={isSubmitting}
                        borderColor={
                          errors.recipients?.[index]?.email ? 'error.500' : 'neutrals.200'
                        }
                        {...register(`recipients.${index}.email`, {
                          required: 'Email is required.',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                            message: 'Invalid email address.'
                          }
                        })}
                      />
                      {errors.recipients?.[index]?.email && (
                        <Text fontSize="14px" color="error.700" mt="5px">
                          {errors.recipients[index].email.message}
                        </Text>
                      )}
                    </Stack>
                    {index !== 0 && (
                      <Button
                        bg="transparent"
                        color="white"
                        fontSize="16px"
                        borderRadius="8px"
                        onClick={() => {
                          setValue(
                            'recipients',
                            getValues('recipients').filter((_, i) => i !== index)
                          );
                        }}
                      >
                        <XCircle size={32} color="#D92D20" />
                      </Button>
                    )}

                    {index === getValues('recipients').length - 1 && (
                      <Button
                        bg="transparent"
                        color="white"
                        fontSize="16px"
                        borderRadius="8px"
                        onClick={() => {
                          const recipients = getValues('recipients');
                          recipients.push({ name: '', email: '' });
                          setValue('recipients', recipients);
                        }}
                      >
                        <PlusCircle size={32} color="#4E76FF" />
                      </Button>
                    )}
                  </Flex>
                ))}
              </Stack>
            </Flex>
            <Flex gap="24px">
              <Text fontSize="16px" fontWeight="semibold" color="neutrals.700" w="10%">
                Subject
              </Text>
              <Stack gap="0" w="100%">
                <Input
                  fontSize="16px"
                  bg="background.secondary"
                  placeholder="Subject"
                  disabled={isSubmitting}
                  borderColor={errors.subject ? 'error.500' : 'neutrals.200'}
                  {...register('subject', { required: 'Subject is required.' })}
                />
                {errors.subject && (
                  <Text fontSize="14px" color="error.700" mt="5px">
                    {errors.subject.message}
                  </Text>
                )}
              </Stack>
            </Flex>
            <Flex gap="24px">
              <Text fontSize="16px" fontWeight="semibold" color="neutrals.700" w="10%">
                Body
              </Text>
              <Stack gap="0" w="100%">
                <Textarea
                  fontSize="16px"
                  bg="background.secondary"
                  placeholder="Enter email body here..."
                  autoresize
                  minH="200px"
                  disabled={isSubmitting}
                  borderColor={errors.body ? 'error.500' : 'neutrals.200'}
                  {...register('body', { required: 'Body is required.' })}
                />
                {errors.body && (
                  <Text fontSize="14px" color="error.700" mt="5px">
                    {errors.body.message}
                  </Text>
                )}
                <Text fontSize="14px" color="neutrals.500" mt="5px">
                  Use {'{{.name}}'} to represent the recipient's name in the email body.
                </Text>
              </Stack>
            </Flex>
            {submitError && (
              <Text fontSize="14px" color="error.700" mt="5px">
                {submitError}
              </Text>
            )}
            <Flex justify="flex-end" gap="20px" mt="24px">
              <Button
                type="button"
                bg="background.secondary"
                color="primary.900"
                fontSize="16px"
                border="1px solid"
                borderColor="primary.900"
                borderRadius="8px"
                p="24px 32px"
                w="150px"
                onClick={() =>
                  confirmAction('Are you sure you want to clear the form?', clearAction)
                }
              >
                Clear
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                bg="primary.900"
                fontSize="16px"
                borderRadius="8px"
                p="24px 32px"
                w="150px"
              >
                Submit
              </Button>
            </Flex>
          </Stack>
        </form>
      </Stack>
    </Flex>
  );
}
